import { DEAD_SIDE_LABEL } from "../lib/bracketDeadSide";
import {
  computeBracketLayout,
  withSeedTwoAtBottom,
  DEFAULT_BRACKET_DIMS,
  type BracketCompetitor,
  type BracketDims,
  type BracketMatch,
  type BracketRound,
  type PositionedMatch,
} from "../lib/bracketLayout";

/**
 * Left-to-right single-elimination bracket, rendered as a self-contained light
 * "draw sheet" (torneos123 print identity) so the on-screen view and the printed
 * PDF look identical. Players sit in rounded "globos"; each matchup is its own
 * card; elbow connectors show which cross feeds the next round; the champion gets
 * a gold box above the final.
 */

const CHAMP_W = 150;
const CHAMP_H = 92;
const CHAMP_GAP = 34;
/** Vertical gap between the final box and the third-place mini-box below it. */
const THIRD_GAP = 46;
/* Aire reservado arriba para la etiqueta de ronda ("Final") antes del globo
 * del campeón. */
const ROUND_LABEL_SPACE = 36;

function Globo({ competitor, source, maxSets }: { competitor: BracketCompetitor | null; source?: string; maxSets: number }) {
  if (!competitor) {
    return (
      <div className="bracket-pro__globo bracket-pro__globo--pending">
        <span className="bracket-pro__seed" />
        <span className="bracket-pro__name">
          {source === DEAD_SIDE_LABEL ? source : source ? `Por definir · ${source}` : "Por definir"}
        </span>
        <span className="bracket-pro__sets" />
      </div>
    );
  }
  // Pareja de dobles ("Ana / Beto"): dos renglones apilados para que ambos
  // nombres se lean completos SIN ensanchar la caja — el fit a 1 hoja del
  // PDF conserva su geometría (pedido Eduardo 2026-07-28).
  const pairParts = competitor.name.includes(" / ")
    ? competitor.name.split(" / ")
    : null;
  return (
    <div className={`bracket-pro__globo${competitor.isWinner ? " bracket-pro__globo--win" : " bracket-pro__globo--lose"}${pairParts ? " bracket-pro__globo--pair" : ""}`}>
      <span className="bracket-pro__seed">{competitor.seed ?? ""}</span>
      {pairParts ? (
        <span className="bracket-pro__name bracket-pro__name--pair" title={competitor.name}>
          {pairParts.map((part, i) => (
            <span key={i}>{part}</span>
          ))}
        </span>
      ) : (
        <span className="bracket-pro__name" title={competitor.name}>{competitor.name}</span>
      )}
      <span className="bracket-pro__sets">
        {Array.from({ length: maxSets }, (_, i) => (
          <span key={i} className="bracket-pro__set">{competitor.games[i] ?? ""}</span>
        ))}
        {competitor.tag ? (
          <span className="bracket-pro__set bracket-pro__set--tag" title={competitor.tag === "BAJA" ? "Jugador dado de baja del torneo" : "Partido no completado"}>{competitor.tag}</span>
        ) : null}
      </span>
    </div>
  );
}

function MatchBox({
  pm,
  dims,
  onMatchClick,
  variant,
  highlighted,
  mine,
}: {
  pm: PositionedMatch;
  dims: BracketDims;
  onMatchClick?: (matchId: number) => void;
  variant?: "third";
  highlighted?: boolean;
  /** Partido del camino del jugador (Draw del jugador: marcado en lima). */
  mine?: boolean;
}) {
  const { match } = pm;
  const maxSets = Math.max(match.top?.games.length ?? 0, match.bottom?.games.length ?? 0);
  const className = `bracket-pro__match${pm.isFinal ? " bracket-pro__match--final" : ""}${variant === "third" ? " bracket-pro__match--third" : ""}${match.pending ? " bracket-pro__match--pending" : ""}${highlighted ? " bracket-pro__match--swap-selected" : ""}${mine ? " bracket-pro__match--mine" : ""}`;
  const style = { left: pm.x, top: pm.y, width: dims.boxW };
  const body = (
    <>
      {match.number != null && (
        <span className="bracket-pro__mnum" aria-label={`Partido ${match.number}`}>
          M{match.number}
        </span>
      )}
      <Globo competitor={match.top} source={match.topSource} maxSets={maxSets} />
      <Globo competitor={match.bottom} source={match.bottomSource} maxSets={maxSets} />
    </>
  );
  if (match.matchId != null && onMatchClick) {
    const matchId = match.matchId;
    return (
      <button type="button" className={`${className} bracket-pro__match--clickable`} style={style} onClick={() => onMatchClick(matchId)}>
        {body}
      </button>
    );
  }
  return (
    <div className={className} style={style}>
      {body}
    </div>
  );
}

export default function EliminationBracket({
  rounds,
  dims = DEFAULT_BRACKET_DIMS,
  onMatchClick,
  thirdPlace,
  highlightMatchId,
  pathMatchIds,
}: {
  rounds: BracketRound[];
  dims?: BracketDims;
  onMatchClick?: (matchId: number) => void;
  /** Optional "3er lugar" match, drawn as a mini-box below the final. */
  thirdPlace?: BracketMatch | null;
  /** Matchup resaltado (modo acomodar: primera selección del swap). */
  highlightMatchId?: number | null;
  /** Draw del jugador: partidos de su camino, marcados en lima. */
  pathMatchIds?: ReadonlySet<number> | null;
}) {
  let layout = computeBracketLayout(rounds, dims);
  if (!layout.matches.length) return null;
  // Cuadro chico (solo Final o Semis+Final): la final queda casi pegada al
  // borde superior y el globo del campeón se pintaba ENCIMA de la final
  // (Eduardo 2026-09-07, Draw RR con 2 clasificados). Se baja todo el
  // cuadro lo justo para que el campeón quepa entre la etiqueta y la final.
  if (layout.champion && layout.finalMatch) {
    const minFinalTop = ROUND_LABEL_SPACE + CHAMP_H + CHAMP_GAP;
    if (layout.finalMatch.y < minFinalTop) {
      layout = computeBracketLayout(rounds, { ...dims, padTop: dims.padTop + (minFinalTop - layout.finalMatch.y) });
    }
  }

  const { width, matches, connectors, roundLabels, champion, finalMatch } = layout;
  let { height } = layout;

  let thirdEl = null;
  if (thirdPlace && finalMatch) {
    const lastRound = rounds.length - 1;
    const thirdY = finalMatch.y + dims.boxH + THIRD_GAP;
    const pm: PositionedMatch = {
      match: withSeedTwoAtBottom(thirdPlace),
      round: lastRound,
      indexInRound: 1,
      x: finalMatch.x,
      y: thirdY,
      cy: thirdY + dims.boxH / 2,
      isFinal: false,
    };
    height = Math.max(height, thirdY + dims.boxH + 16);
    thirdEl = (
      <>
        <div className="bracket-pro__rlabel bracket-pro__rlabel--third" style={{ left: finalMatch.x + dims.boxW / 2, top: thirdY - 20 }}>
          3er lugar
        </div>
        <MatchBox
          pm={pm}
          dims={dims}
          onMatchClick={onMatchClick}
          variant="third"
          mine={!!pathMatchIds && thirdPlace.matchId != null && pathMatchIds.has(thirdPlace.matchId)}
        />
      </>
    );
  }

  let championEl = null;
  if (champion && finalMatch) {
    const centerX = finalMatch.x + dims.boxW / 2;
    const finalTop = finalMatch.y;
    const champBottom = Math.max(CHAMP_H + 6, finalTop - CHAMP_GAP);
    const champTop = champBottom - CHAMP_H;
    championEl = (
      <>
        <div
          className="bracket-pro__champ"
          style={{ left: finalMatch.x + (dims.boxW - CHAMP_W) / 2, top: champTop, width: CHAMP_W, height: CHAMP_H }}
        >
          <div className="bracket-pro__cup" aria-hidden="true">🏆</div>
          <div>
            <small>Campeón</small>
            <strong>
              {champion.name}
              {champion.seed != null ? <span> ({champion.seed})</span> : null}
            </strong>
          </div>
        </div>
        <svg className="bracket-pro__champ-link" style={{ left: 0, top: 0, width, height }} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
          <path d={`M ${centerX},${champBottom} L ${centerX},${finalTop}`} />
        </svg>
      </>
    );
  }

  return (
    <div className="bracket-pro" style={{ width, height }}>
      <svg className="bracket-pro__links" viewBox={`0 0 ${width} ${height}`} width={width} height={height} aria-hidden="true">
        {connectors.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
      {roundLabels.map((label, i) => (
        <div key={i} className="bracket-pro__rlabel" style={{ left: label.x, top: 12 }}>
          {label.text}
        </div>
      ))}
      {matches.map((pm) => (
        <MatchBox
          key={pm.match.id}
          pm={pm}
          dims={dims}
          onMatchClick={onMatchClick}
          highlighted={highlightMatchId != null && pm.match.matchId === highlightMatchId}
          mine={!!pathMatchIds && pm.match.matchId != null && pathMatchIds.has(pm.match.matchId)}
        />
      ))}
      {championEl}
      {thirdEl}
    </div>
  );
}
